/**
 * Minimal StoryQueue contract consumed by FlockInteractionController + StoryPlayer.
 *
 * `createLiveStoryQueue()` fetches real approved stories from Jack's API.
 * `createFixtureStoryQueue()` is the fallback used when the API is unavailable
 * (no dev access yet) or returns zero approved stories.
 *
 * Field names mirror the relevant subset of `Story` / `StoryAnalysis` from
 * `@murmurations/core` — keep them aligned so the real client drops in cleanly.
 *
 * The queue currently plays entries straight through in the order
 * `mapStories()` produces — rating band first (starRating DESC), then
 * shuffled within each band so a given band's stories don't always surface
 * newest-first — looping at the end. `expandWeighted()` remains exported and
 * tested for future re-enablement if we want priority-by-repetition back.
 */

import { AnalyticsEvents, type Story, type CoreTag, type Sentiment } from "@murmurations/core";
import { fetchApprovedStories } from "../api/storiesClient";
import { trackAnalyticsEvent } from "../analytics";
import { MAX_FLOCK_INSTANCES } from "../flock/flockSize";

export interface QueueStory {
  id: string;
  title: string;
  /** Full transcription text (rendered as caption strip during playback). */
  transcription?: string;
  /** Signed audio URL — 1h TTL in production; static for fixtures. */
  audioUrl: string;
  /** Moderator star rating (1–5). 0 = unrated; drives priority weighting. */
  starRating: number;
  /**
   * All core tags assigned to this story by the analysis pipeline. May be
   * empty — not every story matches any of the seven tags. Drives both the
   * bird's dynamic colour (under filter) and the controller's filter gate.
   */
  coreTags: CoreTag[];
  themes?: string[];
  sentiment?: Sentiment;
  fromLocation?: string;
  currentLocation?: string;
  privacyRisk?: number;
  audioQuality?: number;
}

export interface StoryQueue {
  /** Advance to next entry and return it (wraps to start). */
  next(): QueueStory | null;
  /** Step back to previous entry and return it (wraps to end). */
  previous(): QueueStory | null;
  /** Current entry (the last one returned by next/previous), or null. */
  current(): QueueStory | null;
  /**
   * Move the cursor to the entry with `id` and return it, or null if no entry
   * matches. Backs the `?story=<id>` deeplink: leaving the cursor on the
   * matched story means subsequent next()/previous() continue from that bird
   * rather than restarting at the top of the ring.
   */
  seekToId(id: string): QueueStory | null;
  size(): number;
  /**
   * Union of `coreTags` across the underlying entries, in a stable order.
   * FlockManager produces the authoritative filter list (which also covers
   * the random-tag-for-untagged-stories case); this is kept for callers
   * that only have a queue handle and want a quick read of real-story tags.
   */
  getAvailableTags(): CoreTag[];
  /**
   * The underlying unique entries in priority order. FlockManager reads this
   * to build the persistent story↔slot mapping. Returned as a fresh array so
   * callers can't mutate internal state.
   */
  getEntries(): QueueStory[];
}

// ─── Weighted expansion ──────────────────────────────────────────────────────

/**
 * Expand `entries` into a ring of length `targetSize`, where each story
 * repeats with frequency proportional to `max(starRating, 1)`. The
 * Bresenham-style accumulator interleaves picks so 5-star stories appear
 * roughly five times for every one 1-star appearance, with the high-rated
 * stories naturally surfacing earlier in the ring.
 *
 * Exported for direct testing — the weighting is the load-bearing piece of
 * the priority contract, so it earns its own test surface.
 */
export function expandWeighted(entries: QueueStory[], targetSize: number): QueueStory[] {
  if (entries.length === 0 || targetSize <= 0) return [];
  const weights = entries.map((e) => Math.max(e.starRating, 1));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const acc = new Array<number>(entries.length).fill(0);
  const out: QueueStory[] = [];
  for (let k = 0; k < targetSize; k++) {
    let bestIdx = 0;
    for (let i = 0; i < entries.length; i++) {
      acc[i] += weights[i];
      if (acc[i] > acc[bestIdx]) bestIdx = i;
    }
    out.push(entries[bestIdx]);
    acc[bestIdx] -= totalWeight;
  }
  return out;
}

// ─── Mapping from API DTO ────────────────────────────────────────────────────

/**
 * Order entries by rating band — `starRating` DESC so the moderators' star
 * ratings still drive playback priority — but **shuffle within each band**
 * rather than falling back to newest-first. Without this, every session
 * played the same band in createdAt order, so the freshest highly-rated
 * story always came out first; randomising inside the band keeps the rating
 * priority while varying which story leads (Liam, 2026-06-23).
 *
 * The RNG is injectable so the shuffle is deterministic under test; it
 * defaults to `Math.random`. Pure — does not mutate `entries`.
 *
 * Exported for direct testing.
 */
export function orderByRatingBand(
  entries: QueueStory[],
  rng: () => number = Math.random,
): QueueStory[] {
  // Assign each entry a random key, then sort by (band DESC, key ASC). The
  // pre-assigned key means the comparator stays consistent (a fresh rng()
  // per comparison would violate the sort contract), and ties within a band
  // resolve to a uniform random permutation.
  return entries
    .map((entry) => ({ entry, key: rng() }))
    .sort((a, b) => {
      if (b.entry.starRating !== a.entry.starRating) {
        return b.entry.starRating - a.entry.starRating;
      }
      return a.key - b.key;
    })
    .map((x) => x.entry);
}

/**
 * Project a `Story` list to the queue's narrower `QueueStory` shape and order
 * it by rating band (see `orderByRatingBand`). Defensive even though the API
 * already sorts — keeps the ordering contract observable in the file that
 * owns it.
 *
 * Exported for direct testing.
 */
export function mapStories(
  stories: Story[],
  rng: () => number = Math.random,
): QueueStory[] {
  const formatLocation = (city?: string | null, country?: string | null): string | undefined => {
    const parts = [city?.trim(), country?.trim()].filter(Boolean);
    if (parts.length === 0) return undefined;
    return parts.join(", ");
  };

  const projected = stories
    .filter((s) => !!s.audioUrl)
    .map((s) => ({
      id: s.id,
      title: s.analysis?.title ?? `Story ${s.id}`,
      transcription: s.analysis?.transcription,
      audioUrl: s.audioUrl!,
      starRating: s.starRating ?? 0,
      coreTags: s.analysis?.coreTags ?? [],
      themes: s.analysis?.themes ?? [],
      sentiment: s.analysis?.sentiment,
      fromLocation: formatLocation(s.analysis?.originCity, s.analysis?.originCountry),
      currentLocation: formatLocation(s.analysis?.currentCity, s.analysis?.currentCountry),
      privacyRisk: s.analysis?.privacyRisk,
      audioQuality: s.analysis?.audioQuality,
    }));
  return orderByRatingBand(projected, rng);
}

/**
 * The canonical ordering for the tag-filter UI. Matches `CoreTag`'s
 * definition order in `@murmurations/core/types.ts` so designers and back-end
 * folks can reason about the chips in the same order they see in code.
 */
const TAG_ORDER: readonly CoreTag[] = [
  'fear',
  'family',
  'protest',
  'anger',
  'grief',
  'solidarity',
  'hope',
];

function collectAvailableTags(entries: QueueStory[]): CoreTag[] {
  const seen = new Set<CoreTag>();
  for (const e of entries) {
    for (const t of e.coreTags) seen.add(t);
  }
  return TAG_ORDER.filter((t) => seen.has(t));
}

// ─── ArrayStoryQueue (used by fixture + live implementations) ────────────────

class ArrayStoryQueue implements StoryQueue {
  private index = -1;
  private readonly expanded: QueueStory[];
  private readonly availableTags: CoreTag[];

  constructor(entries: QueueStory[], _flockSize: number = MAX_FLOCK_INSTANCES) {
    // Weighted expansion disabled — play entries in API order (rating DESC,
    // newest first) and let next()/previous() wrap via modulo. Swap back to
    // `expandWeighted(entries, _flockSize)` to restore repetition weighting.
    this.expanded = entries.slice();
    this.availableTags = collectAvailableTags(entries);
  }

  next(): QueueStory | null {
    if (this.expanded.length === 0) return null;
    this.index = (this.index + 1) % this.expanded.length;
    return this.expanded[this.index];
  }

  previous(): QueueStory | null {
    if (this.expanded.length === 0) return null;
    this.index = this.index <= 0 ? this.expanded.length - 1 : this.index - 1;
    return this.expanded[this.index];
  }

  current(): QueueStory | null {
    if (this.index < 0 || this.index >= this.expanded.length) return null;
    return this.expanded[this.index];
  }

  seekToId(id: string): QueueStory | null {
    const idx = this.expanded.findIndex((e) => e.id === id);
    if (idx < 0) return null;
    this.index = idx;
    return this.expanded[this.index];
  }

  size(): number {
    return this.expanded.length;
  }

  getAvailableTags(): CoreTag[] {
    return this.availableTags;
  }

  getEntries(): QueueStory[] {
    return this.expanded.slice();
  }
}

/** Build an in-memory queue from a fixed entry list. `flockSize` controls the
 *  virtual ring length — defaults to `MAX_FLOCK_INSTANCES`, override in tests. */
export function createStoryQueue(entries: QueueStory[], flockSize: number = MAX_FLOCK_INSTANCES): StoryQueue {
  return new ArrayStoryQueue(entries, flockSize);
}

// ─── Fixture queue ────────────────────────────────────────────────────────────

/**
 * Placeholder queue used when the API is unavailable or returns no stories.
 * Audio URLs point at public-domain test clips so the playback path can be
 * verified locally without API access. Star ratings + core tags differ across
 * fixtures so the priority weighting and tag filter are observable in `dev:ar`.
 */
export function createFixtureStoryQueue(): StoryQueue {
  return createStoryQueue([
    {
      id: "fixture-1",
      title: "A morning in Boyle Heights",
      transcription: "I woke up early today and the streets were quiet. I thought about my neighbors and the people who came before us.",
      audioUrl: "https://upload.wikimedia.org/wikipedia/commons/c/c8/Example.ogg",
      starRating: 5,
      coreTags: ["hope", "solidarity"],
    },
    {
      id: "fixture-2",
      title: "What I hope for the city",
      transcription: "What I hope for the city is that we can stand together when it matters and remember that we belong to each other.",
      audioUrl: "https://upload.wikimedia.org/wikipedia/commons/c/c8/Example.ogg",
      starRating: 3,
      coreTags: ["solidarity", "protest"],
    },
    {
      id: "fixture-3",
      title: "Letter to my daughter",
      transcription: "This is a letter to my daughter, who will read it long after today. The city will change, and so will you.",
      audioUrl: "https://upload.wikimedia.org/wikipedia/commons/c/c8/Example.ogg",
      starRating: 1,
      coreTags: ["family"],
    },
  ]);
}

// ─── Live queue ───────────────────────────────────────────────────────────────

// Refresh audioUrls if the queue was fetched more than 50 minutes ago.
// The API vends 1-hour TTL signed URLs; 50 min gives a 10-min safety margin.
const REFRESH_THRESHOLD_MS = 50 * 60 * 1000;

class LiveStoryQueue implements StoryQueue {
  private entries: QueueStory[];
  private expanded: QueueStory[];
  private availableTags: CoreTag[];
  private index = -1;
  private fetchedAt: number;
  private refreshing = false;
  private readonly flockSize: number;

  constructor(entries: QueueStory[], flockSize: number = MAX_FLOCK_INSTANCES) {
    this.entries = entries;
    this.flockSize = flockSize;
    // Weighted expansion disabled — see ArrayStoryQueue note.
    this.expanded = entries.slice();
    this.availableTags = collectAvailableTags(entries);
    this.fetchedAt = Date.now();
  }

  private maybeRefresh(): void {
    if (this.refreshing || Date.now() - this.fetchedAt < REFRESH_THRESHOLD_MS) return;
    this.refreshing = true;
    fetchApprovedStories()
      .then((stories) => {
        const fresh = mapStories(stories);
        // Weighted expansion disabled — see ArrayStoryQueue note.
        const freshExpanded = fresh.slice();
        // Re-anchor over the expanded ring so the user's current story id
        // doesn't jump when fresh signed URLs land mid-loop.
        const currentId = this.expanded[this.index]?.id;
        this.entries = fresh;
        this.expanded = freshExpanded;
        this.availableTags = collectAvailableTags(fresh);
        if (currentId && freshExpanded.length > 0) {
          const newIdx = freshExpanded.findIndex((e) => e.id === currentId);
          this.index = newIdx >= 0 ? newIdx : -1;
        } else {
          this.index = -1;
        }
        this.fetchedAt = Date.now();
      })
      .catch((err) => console.warn("[StoryQueue] background refresh failed:", err))
      .finally(() => {
        this.refreshing = false;
      });
  }

  next(): QueueStory | null {
    this.maybeRefresh();
    if (this.expanded.length === 0) return null;
    this.index = (this.index + 1) % this.expanded.length;
    return this.expanded[this.index];
  }

  previous(): QueueStory | null {
    this.maybeRefresh();
    if (this.expanded.length === 0) return null;
    this.index = this.index <= 0 ? this.expanded.length - 1 : this.index - 1;
    return this.expanded[this.index];
  }

  current(): QueueStory | null {
    if (this.index < 0 || this.index >= this.expanded.length) return null;
    return this.expanded[this.index];
  }

  seekToId(id: string): QueueStory | null {
    const idx = this.expanded.findIndex((e) => e.id === id);
    if (idx < 0) return null;
    this.index = idx;
    return this.expanded[this.index];
  }

  size(): number {
    return this.expanded.length;
  }

  getAvailableTags(): CoreTag[] {
    return this.availableTags;
  }

  getEntries(): QueueStory[] {
    return this.expanded.slice();
  }
}

/**
 * Fetches approved stories from the API and returns a live StoryQueue.
 * The queue transparently refreshes signed audioUrls in the background once
 * they approach their 1-hour TTL (threshold: 50 min).
 *
 * Falls back to `createFixtureStoryQueue()` if the API is unreachable or
 * returns no playable stories, so the scene always has something to show.
 */
export async function createLiveStoryQueue(): Promise<StoryQueue> {
  try {
    const stories = await fetchApprovedStories();
    trackAnalyticsEvent(AnalyticsEvents.VIEW_ITEM_LIST, {
      item_list_id: "ar_story_queue",
      item_list_name: "AR Story Queue",
      content_type: "audio_story",
      items_count: stories.length,
    });
    const entries = mapStories(stories);
    stories.map((s) => {
      console.log("[Story]", s);
    });
    if (entries.length > 0) {
      return new LiveStoryQueue(entries);
    }
    console.warn("[StoryQueue] API returned 0 playable stories — using fixture queue");
  } catch (err) {
    console.warn("[StoryQueue] API unavailable — using fixture queue:", err);
  }
  return createFixtureStoryQueue();
}
